import { Color } from '@nect/ui/theme/color';
import { propToBoxShadow } from '@nect/ui/theme/helper';
import styled, { css } from 'styled-components';

import { Box } from '../box';
import { IInputStyledProps, statusColour } from '../input/input.styled';
import {
    TextBodySmallCSS,
    TextButtonLargeCSS,
    TextButtonSmallCSS,
} from '../text/text.styled';

// @TODO Improve break points

const ContainerStyled = styled.div<{ isOpen: boolean }>`
    position: relative;

    ${({ isOpen }) =>
        isOpen
            ? css`
                  @media (max-width: ${({ theme }) =>
                          theme.breakpoint.mobile.max}) {
                      position: fixed;
                      width: 100%;
                      height: 100%;
                      top: 0;
                      left: 0;
                      background: white;
                      z-index: 999;
                  }
              `
            : undefined}
`;

const HeaderStyled = styled.div`
    display: none;

    @media (max-width: ${({ theme }) => theme.breakpoint.mobile.max}) {
        ${TextButtonLargeCSS}
        display: flex;
        align-items: center;
        height: 56px;
        padding: ${({ theme }) => `${theme.spacing.S} ${theme.spacing.M}`};

        border-bottom: 1px solid ${({ theme }) => theme.color[Color.grey500]};

        > i {
            margin-right: ${({ theme }) => theme.spacing.XXS};
        }
    }
`;

const InputStyled = styled.input`
    ${TextBodySmallCSS}

    width: 100%;
    height: 100%;
    border: none;
    outline: none;
    background-color: transparent;
    border-radius: ${({ theme }) => theme.borderRadius.XS};
    padding-left: ${({ theme }) => theme.spacing.S};
    padding-right: 45px;

    cursor: pointer;

    &:focus {
        cursor: text;
    }

    &::placeholder {
        color: ${({ theme }) => theme.color[Color.grey500]};
    }
`;

const LabelStyled = styled.label`
    ${TextButtonSmallCSS}

    position: absolute;
    top: -9px;
    left: ${({ theme }) => theme.spacing.S};

    background: ${({ theme }) => theme.color[Color.white100]};
    padding: ${({ theme }) => `0 ${theme.spacing.XXXS}`};
`;

const BoxStyled = styled(Box)<{ $isOpen: boolean } & IInputStyledProps>`
    ${TextButtonSmallCSS}
    height: 40px;

    background-color: ${({ theme }) => theme.color[Color.white100]};

    box-shadow: ${({ $state, theme, disabled }) =>
        `inset 0 0 0 ${$state === 'default' ? 1 : 2}px ${statusColour({
            $state,
            theme,
            disabled,
        })}`};

    &:focus-within {
        box-shadow: inset 0 0 0 2px
                ${({ theme }) => theme.color[Color.snackInfoBase]},
            0 0 0 2px rgba(10, 108, 255, 0.08);
    }

    &:hover {
        box-shadow: inset 0 0 0 2px
            ${({ theme, disabled }) =>
                theme.color[disabled ? Color.grey100 : Color.grey700]};
    }

    ${({ theme, $isOpen }) =>
        $isOpen &&
        css`
            @media (max-width: ${theme.breakpoint.mobile.max}) {
                margin: ${theme.spacing.M};
                margin-bottom: 0;
            }
        `}
`;

const ToggleButtonStyled = styled.button`
    background-color: transparent;
    border: none;

    position: absolute;
    top: 1px;
    right: 1px;
    padding-right: ${({ theme }) => theme.spacing.S};
    height: 40px;

    display: flex;
    align-items: center;
    outline: none;
    cursor: pointer;
    border-radius: ${({ theme }) => theme.borderRadius.XS};

    &:before {
        content: '';
        width: 20px;
        height: 30px;

        background: linear-gradient(
            270deg,
            #ffffff 60.42%,
            rgba(255, 255, 255, 0.8) 83.03%,
            rgba(255, 255, 255, 0) 100%
        );
    }
`;

const ListContainerStyled = styled(Box)<{ isNotFoundState: boolean }>`
    overflow: hidden;
    margin-top: ${({ theme }) => theme.spacing.S};

    background-color: ${({ theme }) => theme.color[Color.white100]};
    border-color: ${({ theme, isNotFoundState }) =>
        isNotFoundState ? 'transparent' : theme.color[Color.grey300]};
    border-radius: ${({ theme }) => theme.borderRadius.S};

    ${({ theme }) => propToBoxShadow(3, 'light', theme)}

    position: absolute;
    width: 100%;
    z-index: 10;

    > ul {
        padding: 0;
        margin: 0;
    }

    @media (max-width: ${({ theme }) => theme.breakpoint.mobile.max}) {
        border-radius: 0;
        margin: ${({ theme }) => theme.spacing.M};
        width: calc(
            100% - ${({ theme }) => theme.spacing.M} -
                ${({ theme }) => theme.spacing.M}
        );
        box-shadow: none;
        position: relative;
    }
`;

interface IListItemStyledProp {
    isHighlighted: boolean;
    isSelected: boolean;
}
const ListItemStyled = styled.li<IListItemStyledProp>`
    ${TextBodySmallCSS}

    list-style: none;
    min-height: 40px;
    display: flex;
    align-items: center;
    margin: ${({ theme }) => theme.spacing.XXS};
    padding: 0 ${({ theme }) => theme.spacing.S};

    border-radius: ${({ theme }) => theme.borderRadius.S};

    background: ${({ theme, isHighlighted, isSelected }) =>
        theme.color[
            isHighlighted || isSelected ? Color.grey100 : Color.white100
        ]};

    cursor: pointer;

    > span {
        flex: 1;
    }

    > img,
    > i,
    > svg {
        margin-right: ${({ theme }) => theme.spacing.S};
    }
`;
const EmptyStyled = styled.li`
    ${TextButtonSmallCSS}

    list-style: none;
    min-height: 40px;
    display: flex;
    align-items: center;
    padding: ${({ theme }) => `${theme.spacing.XXS} ${theme.spacing.M}`};

    background: ${({ theme }) => theme.color[Color.grey100]};
    color: ${({ theme }) => theme.color[Color.grey500]};

    > img,
    > i,
    > svg {
        margin-right: ${({ theme }) => theme.spacing.S};
    }
`;

// eslint-disable-next-line import/no-default-export
export default {
    ContainerStyled,
    InputStyled,
    LabelStyled,
    BoxStyled,
    ToggleButtonStyled,
    ListContainerStyled,
    ListItemStyled,
    HeaderStyled,
    EmptyStyled,
};
